import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { trackError } from '../lib/analytics'

// Contains failures from a lazily-loaded subtree (e.g. a stale chunk 404 after a deploy) so they can
// never blank the whole page. Renders `fallback` for the failed subtree instead of rethrowing.
// `lazyWithRetry` already retries the import once; this is the final safety net when it still fails.
//
// As a backstop, when the subtree fails it emits an error activity event (trackError) so the failure
// is visible in the admin errors view even though the user only ever sees the inline fallback (e.g.
// "Replay unavailable"). `eventTag` identifies which widget failed; `eventName` overrides the event
// name (default 'chunk-load-failed').
export default class ChunkErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode; eventTag?: string; eventName?: string },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // Best-effort telemetry — recovery was already attempted (lazyWithRetry) and the fallback is
    // sufficient, so we only record the error so it surfaces in the admin errors view.
    try {
      trackError(
        this.props.eventName ?? 'chunk-load-failed',
        {
          message: error instanceof Error ? error.message : String(error),
          componentStack: info?.componentStack?.slice(0, 1000) ?? null,
        },
        this.props.eventTag ?? null,
      )
    } catch {
      /* analytics must never throw into the boundary */
    }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
